import React, { useState, useEffect } from "react";
import { FaIdCard, FaEdit } from "react-icons/fa";
import { MdHourglassEmpty, MdFormatListBulleted } from "react-icons/md";
import { HiOutlineDownload } from "react-icons/hi";
import SideBar from "../../../components/Sidebar";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";
import { useSubscriptionCheck } from "../../../hooks/useSubscriptionCheck";
import SubscriptionDialog from "../../../components/Dialogs/SubscriptionDialog";
import Swal from "sweetalert2";
import NsdlPanCardService from "../../../services/NsdlPanCardService";
import storage from "../../../utils/storage";
import AuthService from "../../../services/AuthService";

const NsdlPanCard = () => {
  const [loading, setLoading] = useState(false);
  const [serviceStatus, setServiceStatus] = useState("");

  const { premiumStatus, showDialog, setShowDialog, checkSubscriptionStatus } =
    useSubscriptionCheck();

  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      try {
        const status = await fetchServiceStatus();

        // ✅ ONLY check subscription if service is NOT enabled
        if (status !== "ENABLED") {
          await checkSubscriptionStatus();
        }
      } finally {
        setLoading(false);
      }
    };
    initialize();
  }, []);

  const fetchServiceStatus = async () => {
    try {
      const result = await NsdlPanCardService.serviceStatus();

      if (!result.success) {
        Swal.fire("Oops!", result.message || "Request failed!", "error");
        return "";
      }

      const res = result.data;

      if (res.status === "SUCCESS") {
        const status = res?.data?.service_status || "";
        setServiceStatus(status);
        return status; // 👈 return it
      }

      if (res.message == "Unauthorized Access") {
        storage.clearStorage();
        window.location.href = "/";
      }
    } catch (error) {
      Swal.fire(
        "Oops!",
        error.response?.data?.message || error.message || "Network error",
        "error",
      );
    }

    return "";
  };

  const canAccessService = () => {
    if (serviceStatus === "ENABLED") return true;
    return premiumStatus === "true";
  };

  const services = [
    {
      name: "New PAN Application",
      icon: <FaIdCard size={40} color="#0d6efd" />,
      href: "/nsdl-pancard/pan-application/49A",
      newTab: false,
    },
    {
      name: "Correction PAN",
      icon: <FaEdit size={40} color="#0d6efd" />,
      href: "/nsdl-pancard/pan-application/CR",
      newTab: false,
    },

    // 🆕 Aadhaar to PAN
    {
      name: "Aadhaar Number to PAN Number",
      icon: <FaIdCard size={40} color="#198754" />,
      href: "/aadhaar-to-pan",
      newTab: false,
      isNew: true,
    },

    {
      name: "PAN History",
      icon: <MdFormatListBulleted size={40} color="#0d6efd" />,
      href: "/nsdl-pancard/history",
    },
    {
      name: "PAN Status",
      icon: <MdHourglassEmpty size={40} color="#0d6efd" />,
      href: "/nsdl-pancard/pan-status",
      newTab: false,
    },
    {
      name: "PAN Status (Protean)",
      icon: <MdHourglassEmpty size={40} color="#0d6efd" />,
      href: "https://tin.tin.proteantech.in/pantan/StatusTrack.html",
      newTab: true,
    },
    {
      name: "e-PAN Download",
      icon: <HiOutlineDownload size={40} color="#0d6efd" />,
      href: "https://onlineservices.proteantech.in/paam/requestAndDownloadEPAN.html",
      newTab: true,
    },
    {
      name: "Agent Certificate",
      icon: <HiOutlineDownload size={40} color="#0d6efd" />,
      action: "DOWNLOAD_CERTIFICATE",
    },
    {
      name: "Photo & Signature Cropping Tool",
      icon: <HiOutlineDownload size={40} color="#0d6efd" />,
      href: "https://nsdl-pan-cropping.ssoaccess.in/",
      newTab: true,
    },
  ];

  const downloadCertificate = async () => {
    setLoading(true);

    try {
      const blob = await NsdlPanCardService.downloadCertificate();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `PAN_Agent_Certificate_${Date.now()}.pdf`;

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      Swal.fire(
        "Oops!",
        error.message || "Certificate download failed",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {loading && <LoadingScreen />}

      <div className="container-fluid mt-3">
        <div className="row">
          <SideBar />

          <div className="col-md-9 mb-3">
            <div className="card mb-4 shadow-sm">
              <div className="card-body">
                <h5 className="card-title mb-4">NSDL PAN Card Services</h5>

                <div className="d-flex gap-4 flex-wrap justify-content-center">
                  {services.map((service, index) => (
                    <div
                      key={index}
                      className="border rounded p-4 text-center shadow-sm hover-effect"
                      style={{
                        width: "220px",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                      }}
                      onClick={() => {
                        if (!canAccessService()) {
                          setShowDialog(true);
                          return;
                        }

                        if (service.action === "DOWNLOAD_CERTIFICATE") {
                          downloadCertificate();
                          return;
                        }

                        service.newTab
                          ? window.open(service.href, "_blank")
                          : (window.location.href = service.href);
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.transform = "scale(1.05)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.transform = "scale(1)")
                      }
                    >
                      <div className="mb-3">{service.icon}</div>
                      <div className="fw-bold">
                        {service.name}{" "}
                        {service.isNew && (
                          <span
                            className="badge bg-danger ms-2"
                            style={{ fontSize: "10px" }}
                          >
                            NEW
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="card shadow-sm mt-4">
              <div className="card-body">
                <h5 className="card-title mb-3">Video Tutorials</h5>

                {/* ===== E-KYC MODE ROW ===== */}
                <h6 className="mb-2 text-primary pt-3">
                  Agent/Branch Registration
                </h6>
                <div className="d-flex gap-4 flex-wrap justify-content-center mb-4">
                  {/* New PAN - E-KYC */}
                  <div
                    className="border rounded p-3 text-center shadow-sm hover-effect"
                    style={{ width: "260px", cursor: "pointer" }}
                    onClick={() =>
                      window.open(
                        "https://drive.google.com/file/d/1W3xsmvx2gKu5kiEC_EjF6tdM3jxBGfH-/view?usp=sharing",
                        "_blank",
                      )
                    }
                  >
                    <div className="fw-medium">Agent/ Branch Registration</div>
                  </div>
                </div>

                {/* ===== E-KYC MODE ROW ===== */}
                <h6 className="mb-2 text-primary pt-3">
                  E-KYC Mode (Aadhaar OTP/Fingerprint Based)
                </h6>
                <div className="d-flex gap-4 flex-wrap justify-content-center mb-4">
                  {/* New PAN - E-KYC */}
                  <div
                    className="border rounded p-3 text-center shadow-sm hover-effect"
                    style={{ width: "260px", cursor: "pointer" }}
                    onClick={() =>
                      window.open(
                        "https://drive.google.com/file/d/1RsDM0eolC1NBSmNQ8qfDT9_Hi6ByO3s-/view?usp=drive_link",
                        "_blank",
                      )
                    }
                  >
                    <div className="fw-medium">New PAN Apply (E-KYC Mode)</div>
                  </div>

                  {/* Correction PAN - E-KYC */}
                  <div
                    className="border rounded p-3 text-center shadow-sm hover-effect"
                    style={{ width: "260px", cursor: "pointer" }}
                    onClick={() =>
                      window.open(
                        "https://drive.google.com/file/d/1RsDM0eolC1NBSmNQ8qfDT9_Hi6ByO3s-/view?usp=drive_link",
                        "_blank",
                      )
                    }
                  >
                    <div className="fw-medium">PAN Correction (E-KYC Mode)</div>
                  </div>
                </div>

                {/* ===== E-SIGN MODE ROW ===== */}
                <h6 className="mb-2 text-primary">
                  E-Sign Mode (Photo & Signature Upload)
                </h6>
                <div className="d-flex gap-4 flex-wrap justify-content-center">
                  {/* New PAN - E-Sign */}
                  <div
                    className="border rounded p-3 text-center shadow-sm hover-effect"
                    style={{ width: "260px", cursor: "pointer" }}
                    onClick={() =>
                      window.open(
                        "https://drive.google.com/file/d/1RX3Z3vIu1LzumEl3RIJUX8FZjkzVcZCK/view?usp=sharing",
                        "_blank",
                      )
                    }
                  >
                    <div className="fw-medium">New PAN Apply (E-Sign Mode)</div>
                  </div>

                  {/* Correction PAN - E-Sign */}
                  <div
                    className="border rounded p-3 text-center shadow-sm hover-effect"
                    style={{ width: "260px", cursor: "pointer" }}
                    onClick={() =>
                      window.open(
                        "https://drive.google.com/file/d/1syq6ObD1iZK0qAQpS1v3sws6fr5nf8Zo/view?usp=drive_link",
                        "_blank",
                      )
                    }
                  >
                    <div className="fw-medium">
                      PAN Correction (E-Sign Mode)
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <SubscriptionDialog
        show={showDialog && serviceStatus !== "ENABLED"}
        onHide={() => setShowDialog(false)}
        serviceName="NSDL PAN"
      />
    </div>
  );
};

export default NsdlPanCard;
